import axios from 'axios';

// WooCommerce API Configuration
const WOOCOMMERCE_URL = process.env.REACT_APP_WOOCOMMERCE_URL || 'https://shop.bailebelle.com/wp-json/wc/v3';
const CONSUMER_KEY = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY || 'ck_1b59a3bacdd5e54a2f05b3871e8d781caaf11385';
const CONSUMER_SECRET = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET || 'cs_0f97862a82893c1328eb3cfb664b65021cc4f5bb';

// Debug environment variables (commented out for production)
// console.log('WooCommerce API Configuration:', {
//   url: WOOCOMMERCE_URL,
//   hasKey: !!CONSUMER_KEY,
//   hasSecret: !!CONSUMER_SECRET,
//   keyPrefix: CONSUMER_KEY?.substring(0, 10) + '...',
//   secretPrefix: CONSUMER_SECRET?.substring(0, 10) + '...'
// });

// Create axios instance with base configuration
const woocommerce = axios.create({
  baseURL: WOOCOMMERCE_URL,
  auth: {
    username: CONSUMER_KEY,
    password: CONSUMER_SECRET,
  },
  timeout: 30000, // 30 seconds timeout for cPanel compatibility
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor for logging and error handling
woocommerce.interceptors.request.use(
  (config) => {
    // console.log(`Making API request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with retry logic
woocommerce.interceptors.response.use(
  (response) => {
    // console.log('API Response received:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    console.error('API Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
    });

    // Retry logic for specific errors
    if (error.response?.status >= 500 && error.config && !error.config._retry) {
      error.config._retry = true;
      console.log('Retrying API call...');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      return woocommerce(error.config);
    }

    return Promise.reject(error);
  }
);

// Cache for API responses
const cache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes for better performance

// Request queue to prevent too many simultaneous requests
const requestQueue = new Map();
const MAX_CONCURRENT_REQUESTS = 3;
let activeRequests = 0;

// Cache helper functions
const getCacheKey = (endpoint, params = {}) => {
  return `${endpoint}_${JSON.stringify(params)}`;
};

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Request throttling function
const throttledRequest = async (requestFn, cacheKey) => {
  // Check if request is already in progress
  if (requestQueue.has(cacheKey)) {
    return requestQueue.get(cacheKey);
  }

  // Check if we have too many active requests
  if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
    // Wait a bit before making the request
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  activeRequests++;
  const requestPromise = requestFn().finally(() => {
    activeRequests--;
    requestQueue.delete(cacheKey);
  });

  requestQueue.set(cacheKey, requestPromise);
  return requestPromise;
};

// API Methods
export const wooAPI = {
  // Products with optimized parameters
  getProducts: async (params = {}) => {
    const defaultParams = {
      per_page: Math.min(params.per_page || 8, 20), // Limit max per_page to 20
      status: 'publish',
      orderby: 'date',
      order: 'desc',
      ...params,
    };
    const cacheKey = getCacheKey('/products', defaultParams);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached products data');
      return cachedData;
    }

    return throttledRequest(async () => {
      try {
        const response = await woocommerce.get('/products', { params: defaultParams });
        setCachedData(cacheKey, response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching products:', error);
        return [];
      }
    }, cacheKey);
  },

  getProduct: async (id) => {
    const cacheKey = getCacheKey(`/products/${id}`);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached product data');
      return cachedData;
    }

    const response = await woocommerce.get(`/products/${id}`);
    setCachedData(cacheKey, response.data);
    return response.data;
  },

  // Get product by slug - more efficient method
  getProductBySlug: async (slug) => {
    if (!slug) {
      console.error('No slug provided to getProductBySlug');
      return null;
    }

    const cacheKey = getCacheKey(`/products/slug/${slug}`);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached product data by slug');
      return cachedData;
    }

    try {
      console.log(`Searching for product with slug: "${slug}"`);

      // First try to search for the product by slug
      const searchResults = await woocommerce.get('/products', {
        params: {
          search: slug,
          per_page: 20, // Increased from 10 to 20
          status: 'publish'
        }
      });

      console.log(`Search returned ${searchResults.data.length} products`);
      const product = searchResults.data.find(p => p.slug === slug);
      if (product) {
        console.log(`Found product by search: ${product.name} (ID: ${product.id})`);
        setCachedData(cacheKey, product);
        return product;
      }

      // If not found by search, try fetching more products
      console.log('Product not found by search, trying larger batch...');
      const moreResults = await woocommerce.get('/products', {
        params: {
          per_page: 100,
          status: 'publish'
        }
      });

      console.log(`Large batch returned ${moreResults.data.length} products`);
      const foundProduct = moreResults.data.find(p => p.slug === slug);
      if (foundProduct) {
        console.log(`Found product in large batch: ${foundProduct.name} (ID: ${foundProduct.id})`);
        setCachedData(cacheKey, foundProduct);
        return foundProduct;
      }

      // If still not found, try an even larger batch
      console.log('Product not found in 100 products, trying 200...');
      const evenMoreResults = await woocommerce.get('/products', {
        params: {
          per_page: 200,
          status: 'publish'
        }
      });

      console.log(`Extra large batch returned ${evenMoreResults.data.length} products`);
      const finalProduct = evenMoreResults.data.find(p => p.slug === slug);
      if (finalProduct) {
        console.log(`Found product in extra large batch: ${finalProduct.name} (ID: ${finalProduct.id})`);
        setCachedData(cacheKey, finalProduct);
        return finalProduct;
      }

      console.log(`Product with slug "${slug}" not found in any batch`);
      return null;
    } catch (error) {
      console.error('Error fetching product by slug:', error);
      return null;
    }
  },

  getProductVariations: async (productId) => {
    const cacheKey = getCacheKey(`/products/${productId}/variations`);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached variations data');
      return cachedData;
    }

    const response = await woocommerce.get(`/products/${productId}/variations`);
    setCachedData(cacheKey, response.data);
    return response.data;
  },

  // Categories
  getCategories: async (params = {}) => {
    const defaultParams = {
      per_page: 100,
      hide_empty: true,
      ...params,
    };
    const cacheKey = getCacheKey('/products/categories', defaultParams);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached categories data');
      return cachedData;
    }

    const response = await woocommerce.get('/products/categories', { params: defaultParams });
    setCachedData(cacheKey, response.data);
    return response.data;
  },

  getCategory: async (id) => {
    const cacheKey = getCacheKey(`/products/categories/${id}`);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached category data');
      return cachedData;
    }

    const response = await woocommerce.get(`/products/categories/${id}`);
    setCachedData(cacheKey, response.data);
    return response.data;
  },

  // Orders
  createOrder: async (orderData) => {
    const response = await woocommerce.post('/orders', orderData);
    return response.data;
  },

  getOrder: async (id) => {
    const response = await woocommerce.get(`/orders/${id}`);
    return response.data;
  },

  // Get orders by customer email
  getOrdersByCustomer: async (customerEmail, params = {}) => {
    const defaultParams = {
      customer: customerEmail,
      per_page: 20,
      orderby: 'date',
      order: 'desc',
      ...params,
    };
    const response = await woocommerce.get('/orders', { params: defaultParams });
    return response.data;
  },

  // Get orders with filtering
  getOrders: async (params = {}) => {
    const defaultParams = {
      per_page: 20,
      orderby: 'date',
      order: 'desc',
      ...params,
    };
    const response = await woocommerce.get('/orders', { params: defaultParams });
    return response.data;
  },

  // Coupons
  getCoupons: async (params = {}) => {
    const response = await woocommerce.get('/coupons', { params });
    return response.data;
  },

  // Customer
  createCustomer: async (customerData) => {
    const response = await woocommerce.post('/customers', customerData);
    return response.data;
  },

  getCustomer: async (id) => {
    const response = await woocommerce.get(`/customers/${id}`);
    return response.data;
  },

  updateCustomer: async (id, customerData) => {
    const response = await woocommerce.put(`/customers/${id}`, customerData);
    return response.data;
  },

  // Reports
  getSalesReport: async (params = {}) => {
    const response = await woocommerce.get('/reports/sales', { params });
    return response.data;
  },

  // Search with debouncing
  searchProducts: async (query, params = {}) => {
    // Don't search for very short queries
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchParams = {
      search: query.trim(),
      per_page: 8, // Reduced from 12 to 8 for better performance
      status: 'publish',
      ...params,
    };
    const cacheKey = getCacheKey('/products/search', searchParams);
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      console.log('Using cached search data');
      return cachedData;
    }

    try {
      const response = await woocommerce.get('/products', { params: searchParams });
      setCachedData(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Search error:', error);
      return []; // Return empty array instead of throwing error
    }
  },

  // Cache management
  clearCache: () => {
    cache.clear();
    console.log('Cache cleared');
  },

  clearProductCache: (productId) => {
    const keysToDelete = [];
    for (const key of cache.keys()) {
      if (key.includes(`/products/${productId}`) || key.includes(`/products/${productId}/variations`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => cache.delete(key));
    console.log(`Cleared cache for product ${productId}`);
  },
};

// Utility functions
export const formatPrice = (price, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(price);
};

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default woocommerce;
